<script>
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGarageStore } from '@/stores/garage.js'
import { useModalStore } from '@/stores/modal.js'
import { isVehicleCreated } from '@/lib/vehicle_helper.js'
import SkeletonLoader from '@/components/ui/skeleton_loader.vue'

export default {
  components: {
    SkeletonLoader
  },
  setup() {
    // I18n
    const { t } = useI18n()

    // Stores
    const garageStore = useGarageStore();
    const modalStore = useModalStore();

    // Variables
    const isLoading = ref(false);

    // Watch for vehicle changes to show loading state
    watch(() => garageStore.currentVehicle, (newVehicle, oldVehicle) => {
      if (oldVehicle && newVehicle && oldVehicle.id !== newVehicle.id) {
        isLoading.value = true;
        setTimeout(() => {
          isLoading.value = false;
        }, 300);
      }
    });

    onMounted(async () => { })

    const onFileChange = (event) => {
      garageStore.updateVehicleImage(event.target.files[0]);
    }

    return {
      t,
      isLoading,
      garageStore,
      modalStore,
      isVehicleCreated,
      onFileChange,
    }
  }
}
</script>

<template>
  <!-- Skeleton Loader -->
  <div v-if="isLoading" class="flex flex-col space-y-6">
    <!-- Garage Info Skeleton -->
    <div class="vehicle-info-card rounded-lg p-4">
      <div class="animate-pulse">
        <div class="flex flex-row border-b pb-4 mb-4 border-gray-300">
          <div class="h-6 bg-gray-200 rounded w-1/3"></div>
          <div class="h-5 bg-gray-200 rounded w-16 ml-4"></div>
        </div>
        <div class="space-y-2">
          <div class="h-5 bg-gray-200 rounded w-1/2"></div>
          <div class="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    </div>

    <!-- Vehicle Info Skeleton -->
    <div class="vehicle-info-card rounded-lg p-4">
      <div class="animate-pulse">
        <div class="flex flex-row border-b pb-4 mb-4 border-gray-300">
          <div class="h-6 bg-gray-200 rounded w-1/3"></div>
          <div class="h-5 bg-gray-200 rounded w-16 ml-4"></div>
        </div>
        <div class="flex flex-col md:flex-row">
          <div class="flex flex-col space-y-4 w-full md:w-1/2">
            <div class="h-6 bg-gray-200 rounded w-3/4"></div>
            <div class="space-y-2">
              <div class="h-4 bg-gray-200 rounded w-1/4"></div>
              <div class="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
            <div class="space-y-2">
              <div class="h-4 bg-gray-200 rounded w-1/4"></div>
              <div class="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
            <div class="space-y-2">
              <div class="h-4 bg-gray-200 rounded w-1/4"></div>
              <div class="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
          <div class="flex flex-col w-full md:w-1/2 mt-4 md:mt-0 md:ml-8">
            <div class="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
            <div class="w-full h-48 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Actual Content -->
  <div class="flex flex-col space-y-6" v-else-if="garageStore.currentVehicle">
    <!-- Garage Info -->
    <div class="vehicle-info-card rounded-lg p-4">
      <div class="flex flex-row border-b pb-4 mb-4 border-gray-300">
        <p class="text-xl font-semibold">{{ t('my_garage.garage_info_section.garage_info') }}</p>
        <button
          v-if="isVehicleCreated(garageStore.currentVehicle, t)"
          class="generic-text-button ml-4 text-lg font-medium"
          @click="modalStore.setModal(modalStore.modalTypeConstants.GARAGE_INFO, garageStore.userGarage)"
        >{{ t('my_garage.edit') }}</button>
      </div>
      <div class="flex flex-col">
        <p class="text-lg font-semibold">{{ garageStore.userGarage && garageStore.userGarage.name ? garageStore.userGarage.name : t('my_garage.garage_info_section.garage_name') }}</p>
        <p>{{ garageStore.userGarage && garageStore.userGarage.description ? garageStore.userGarage.description : t('my_garage.garage_info_section.garage_description') }}</p>
      </div>
    </div>

    <!-- Vehicle Info -->
    <div class="vehicle-info-card rounded-lg p-4">
      <div class="flex flex-row border-b pb-4 mb-4 border-gray-300">
        <p class="text-xl font-semibold">{{ t('my_garage.vehicle_info_section.vehicle_info') }}</p>
        <button
          v-if="isVehicleCreated(garageStore.currentVehicle, t)"
          class="generic-text-button ml-4 text-lg font-medium"
          @click="modalStore.setModal(modalStore.modalTypeConstants.VEHICLE_INFO, garageStore.currentVehicle)"
        >{{ t('my_garage.edit') }}</button>
      </div>
      <div class="flex flex-col md:flex-row">
        <div class="flex flex-col space-y-4 w-full md:w-1/2">
          <div class="flex flex-col">
            <div class="flex flex-row">
              <p class="text-lg font-bold">
                <span>{{ garageStore.currentVehicle && garageStore.currentVehicle.full_vehicle_name_with_engine ? garageStore.currentVehicle.full_vehicle_name_with_engine : t('my_garage.vehicle_info_section.vehicle_year') }}</span>
              </p>
            </div>
          </div>
          <div class="flex flex-col">
            <p class="select-vehicle font-semibold uppercase">{{ t('my_garage.vehicle_info_section.vehicle_nickname') }}</p>
            <div class="flex flex-row">
              <p>
                <span>{{ garageStore.currentVehicle && garageStore.currentVehicle.nickname ? garageStore.currentVehicle.nickname : t('my_garage.vehicle_info_section.vehicle_nickname_placeholder') }}</span>
              </p>
            </div>
          </div>
          <div class="flex flex-col">
            <p class="select-vehicle font-semibold uppercase">{{ t('my_garage.vehicle_info_section.vehicle_description') }}</p>
            <p>{{ garageStore.currentVehicle && garageStore.currentVehicle.description ? garageStore.currentVehicle.description : t('my_garage.vehicle_info_section.vehicle_description_placeholder') }}</p>
          </div>
          <div class="flex flex-col">
            <p class="select-vehicle font-semibold uppercase">{{ t('my_garage.vehicle_info_section.vehicle_modifications') }}</p>
            <p>{{ garageStore.currentVehicle && garageStore.currentVehicle.modifications ? garageStore.currentVehicle.modifications : t('my_garage.vehicle_info_section.vehicle_modifications_placeholder') }}</p>
          </div>
        </div>
        <div class="flex flex-col justify-center w-full md:w-1/2 mt-4 md:mt-0 md:ml-8">
          <div class="flex flex-row mb-1">
            <p class="select-vehicle font-semibold uppercase">{{ t('my_garage.vehicle_info_section.vehicle_image') }}</p>
            <label
              v-if="isVehicleCreated(garageStore.currentVehicle, t)"
              class="cursor-pointer"
            >
              <input :disabled="!isVehicleCreated(garageStore.currentVehicle, t)" type="file" @change="onFileChange" class="hidden">
              <span class="generic-text-button ml-2 font-medium">{{ t('my_garage.update') }}</span>
            </label>
          </div>
          <div v-if="garageStore.currentVehicle && garageStore.currentVehicle.image_url">
            <img :src="garageStore.currentVehicle.image_url" class="w-full object-cover rounded-lg" />
          </div>
          <div v-else class="w-full upload-vehicle-image-container rounded-lg justify-center items-center mt-2">
            <label class="flex flex-col items-center justify-center w-full h-48 rounded-md cursor-pointer">
              <img src="../../../assets/icons/add_circle_24dp_FILL0_wght400_GRAD0_opsz24.svg" alt="Upload Icon" class="h-12 w-12">
              <input :disabled="!isVehicleCreated(garageStore.currentVehicle, t)" type="file" @change="onFileChange" class="hidden">
              <p class="upload-vehicle-image text-lg font-bold mt-2">{{ t('my_garage.vehicle_info_section.vehicle_image_upload') }}</p>
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Original Manufacturer Specs -->
    <!-- <div class="vehicle-info-card rounded-lg p-4">
      <div class="flex flex-row border-b pb-4 mb-4 border-gray-300">
        <p class="text-xl font-semibold">{{ t('my_garage.vehicle_specs_section.vehicle_specs') }}</p>
      </div>
      <div class="flex flex-col">
        <p class="text-lg font-semibold">{{ t('my_garage.vehicle_specs_section.oem_spec') }}</p>
      </div>
      <div class="container mt-2">
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-4">
          <div class="flex flex-col px-0 sm:px-2 space-y-1">
            <p class="font-semibold">{{ t('my_garage.vehicle_specs_section.vehicle_identification.vehicle_identification') }}</p>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.vehicle_identification.year') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.year ? garageStore.currentVehicle.year : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.vehicle_identification.make') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.make ? garageStore.currentVehicle.make : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.vehicle_identification.model') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.model ? garageStore.currentVehicle.model : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.vehicle_identification.trim') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.trim ? garageStore.currentVehicle.trim : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.vehicle_identification.body_type') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.body_type ? garageStore.currentVehicle.specifications.body_type : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.vehicle_identification.vehicle_type') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.vehicle_type ? garageStore.currentVehicle.specifications.vehicle_type : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.vehicle_identification.doors') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.doors ? garageStore.currentVehicle.specifications.doors : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.vehicle_identification.msrp') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.msrp ? garageStore.currentVehicle.specifications.msrp : '-' }}</p>
            </div>
          </div>
          <div class="flex flex-col px-0 sm:px-2 space-y-1">
            <p class="font-semibold">{{ t('my_garage.vehicle_specs_section.specifications.specifications') }}</p>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.specifications.length') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.length ? garageStore.currentVehicle.specifications.length : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.specifications.width') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.width ? garageStore.currentVehicle.specifications.width : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.specifications.height') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.height ? garageStore.currentVehicle.specifications.height : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.specifications.wheel_base') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.wheel_base ? garageStore.currentVehicle.specifications.wheel_base : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.specifications.tank') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.tank ? garageStore.currentVehicle.specifications.tank : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.specifications.curb_wt') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.curb_weight ? garageStore.currentVehicle.specifications.curb_weight : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.specifications.wheel_diameter') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.wheel_diameter ? garageStore.currentVehicle.specifications.wheel_diameter : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.specifications.rr_leg_room') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.rear_leg_room ? garageStore.currentVehicle.specifications.rear_leg_room : '-' }}</p>
            </div>
          </div>
          <div class="flex flex-col px-0 sm:px-2 space-y-1">
            <div class="flex flex-row">
              <p class="font-semibold">{{ t('my_garage.vehicle_specs_section.drivetrain.drivetrain') }}</p>
              <button
                v-if="isVehicleCreated(garageStore.currentVehicle, t)"
                class="generic-text-button ml-4 font-medium"
                @click="modalStore.setModal(modalStore.modalTypeConstants.DRIVETRAIN_INFO, garageStore.currentVehicle)"
              >{{ t('my_garage.edit') }}</button>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.drivetrain.config') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.engine_config ? garageStore.currentVehicle.specifications.engine_config : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.drivetrain.cylinders') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.cylinders ? garageStore.currentVehicle.specifications.cylinders : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.drivetrain.displacement') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.displacement ? garageStore.currentVehicle.specifications.displacement : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.drivetrain.aspiration') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.aspiration ? garageStore.currentVehicle.specifications.aspiration : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.drivetrain.fuel_type') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.fuel_type ? garageStore.currentVehicle.specifications.fuel_type : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.drivetrain.trans_type') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.trans_type ? garageStore.currentVehicle.specifications.trans_type : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.drivetrain.trans_speed') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.trans_speed ? garageStore.currentVehicle.specifications.trans_speed : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.drivetrain.drivetrain') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.drivetrain ? garageStore.currentVehicle.specifications.drivetrain : '-' }}</p>
            </div>
          </div>
          <div class="flex flex-col px-0 sm:px-2 space-y-1">
            <div class="flex flex-row">
              <p class="font-semibold">{{ t('my_garage.vehicle_specs_section.design.design') }}</p>
              <button
                v-if="isVehicleCreated(garageStore.currentVehicle, t)"
                class="generic-text-button ml-4 font-medium"
                @click="modalStore.setModal(modalStore.modalTypeConstants.DESIGN_INFO, garageStore.currentVehicle)"
              >{{ t('my_garage.edit') }}</button>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.design.exterior') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.exterior ? garageStore.currentVehicle.specifications.exterior : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.design.oem_name') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.oem_exterior ? garageStore.currentVehicle.specifications.oem_exterior : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.design.order_code') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.oem_exterior_code ? garageStore.currentVehicle.specifications.oem_exterior_code : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.design.interior') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.interior ? garageStore.currentVehicle.specifications.interior : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.design.oem_name') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.oem_interior ? garageStore.currentVehicle.specifications.oem_interior : '-' }}</p>
            </div>
            <div class="flex flex-row">
              <p class="w-1/2">{{ t('my_garage.vehicle_specs_section.design.order_code') }}:</p>
              <p class="w-1/2">{{ garageStore.currentVehicle && garageStore.currentVehicle.specifications && garageStore.currentVehicle.specifications.oem_interior_code ? garageStore.currentVehicle.specifications.oem_interior_code : '-' }}</p>
            </div>
          </div>
        </div>
      </div>
    </div> -->
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/views/my_garage/my_garage';
</style>
